<template>
  <div class="radar-chart-container">
    <div class="chart-header">
      <h4>Performance Overview</h4>
      <div class="chart-controls">
        <b-form-select
          v-model="selectedComparison"
          size="sm"
          class="comparison-select"
        >
          <b-form-select-option value="pb"
            >vs Personal Best</b-form-select-option
          >
          <b-form-select-option value="avg"
            >vs Season Average</b-form-select-option
          >
          <b-form-select-option value="last"
            >vs Last Session</b-form-select-option
          >
        </b-form-select>
      </div>
    </div>
    <div class="chart-wrapper">
      <Radar
        :chart-options="chartOptions"
        :chart-data="computedChartData"
        :chart-id="chartId"
        :style="chartStyles"
      />
    </div>
    <div class="chart-legend">
      <div class="legend-item current">
        <span class="legend-color"></span>
        <span>Current Session</span>
      </div>
      <div class="legend-item comparison">
        <span class="legend-color"></span>
        <span>{{ comparisonLabel }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { Radar } from "vue-chartjs";
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
);

export default {
  name: "RadarPerformanceChart",
  components: {
    Radar,
  },
  props: {
    gpsData: {
      type: Object,
      default: () => ({}),
    },
    personalBest: {
      type: Object,
      default: () => ({}),
    },
    seasonAverage: {
      type: Object,
      default: () => ({}),
    },
    lastSession: {
      type: Object,
      default: () => ({}),
    },
    gpsType: {
      type: String,
      default: "catapult", // or "playermaker"
    },
    chartId: {
      type: String,
      default: "radar-performance-chart",
    },
  },
  data() {
    return {
      selectedComparison: "pb",
    };
  },
  computed: {
    chartStyles() {
      return {
        height: "400px",
        width: "100%",
      };
    },
    comparisonLabel() {
      const labels = {
        pb: "Personal Best",
        avg: "Season Average",
        last: "Last Session",
      };
      return labels[this.selectedComparison];
    },
    chartLabels() {
      if (this.gpsType === "catapult") {
        return [
          "Total Distance",
          "High Speed Running",
          "Accelerations",
          "Decelerations",
          "Intensity",
          "Top Speed",
        ];
      } else {
        return [
          "Total Distance",
          "Work Rate",
          "Total Touches",
          "Total Passes",
          "Session Duration",
          "Top Speed",
        ];
      }
    },
    normalizedCurrentData() {
      return this.normalizeGPSData(this.gpsData);
    },
    normalizedComparisonData() {
      const comparisonData = {
        pb: this.personalBest,
        avg: this.seasonAverage,
        last: this.lastSession,
      }[this.selectedComparison];

      return this.normalizeGPSData(comparisonData);
    },
    computedChartData() {
      return {
        labels: this.chartLabels,
        datasets: [
          {
            label: "Current Session",
            data: this.normalizedCurrentData,
            backgroundColor: "rgba(71, 169, 249, 0.2)",
            borderColor: "rgba(71, 169, 249, 1)",
            borderWidth: 2,
            pointBackgroundColor: "rgba(71, 169, 249, 1)",
            pointBorderColor: "#fff",
            pointBorderWidth: 2,
            pointRadius: 5,
            pointHoverRadius: 7,
            fill: true,
          },
          {
            label: this.comparisonLabel,
            data: this.normalizedComparisonData,
            backgroundColor: "rgba(255, 99, 132, 0.1)",
            borderColor: "rgba(255, 99, 132, 0.8)",
            borderWidth: 2,
            borderDash: [5, 5],
            pointBackgroundColor: "rgba(255, 99, 132, 0.8)",
            pointBorderColor: "#fff",
            pointBorderWidth: 2,
            pointRadius: 4,
            pointHoverRadius: 6,
            fill: true,
          },
        ],
      };
    },
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false, // We use custom legend
          },
          tooltip: {
            enabled: true,
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            titleColor: "white",
            bodyColor: "white",
            borderColor: "rgba(255, 255, 255, 0.1)",
            borderWidth: 1,
            cornerRadius: 8,
            callbacks: {
              title: (context) => {
                return context[0].label;
              },
              label: (context) => {
                const rawValue = this.getRawValue(
                  context.label,
                  context.datasetIndex,
                );
                const unit = this.getMetricUnit(context.label);
                return `${context.dataset.label}: ${rawValue}${unit}`;
              },
              afterBody: (context) => {
                if (context.length === 2) {
                  const current = context[0].parsed.r;
                  const comparison = context[1].parsed.r;
                  const difference = (
                    ((current - comparison) / comparison) *
                    100
                  ).toFixed(1);
                  const direction = difference > 0 ? "better" : "worse";
                  return `${Math.abs(difference)}% ${direction} than ${this.comparisonLabel.toLowerCase()}`;
                }
                return [];
              },
            },
          },
        },
        scales: {
          r: {
            beginAtZero: true,
            max: 100,
            min: 0,
            ticks: {
              stepSize: 20,
              callback: function (value) {
                return value + "%";
              },
              color: "#666",
              font: {
                size: 10,
              },
            },
            grid: {
              color: "rgba(0, 0, 0, 0.1)",
            },
            angleLines: {
              color: "rgba(0, 0, 0, 0.1)",
            },
            pointLabels: {
              color: "#333",
              font: {
                size: 12,
                weight: "600",
              },
            },
          },
        },
        interaction: {
          intersect: false,
          mode: "point",
        },
        animation: {
          duration: 1200,
          easing: "easeInOutQuart",
        },
      };
    },
  },
  methods: {
    normalizeGPSData(data) {
      if (!data || Object.keys(data).length === 0) {
        return new Array(this.chartLabels.length).fill(0);
      }

      if (this.gpsType === "catapult") {
        return this.normalizeCatapultData(data);
      } else {
        return this.normalizePlayerMakerData(data);
      }
    },
    normalizeCatapultData(data) {
      // Normalize to 0-100 scale based on typical performance ranges
      const normalized = [];

      // Total Distance (0-12000m)
      normalized.push(
        Math.min(((data.session_distance || 0) / 12000) * 100, 100),
      );

      // High Speed Running (0-1500m)
      normalized.push(
        Math.min(
          ((data.total_absolute_high_speed_distance || 0) / 1500) * 100,
          100,
        ),
      );

      // Accelerations (0-150)
      normalized.push(
        Math.min(((data.accelerations_total_efforts || 0) / 150) * 100, 100),
      );

      // Decelerations (0-150)
      normalized.push(
        Math.min(((data.decelerations_total_efforts || 0) / 150) * 100, 100),
      );

      // Intensity (0-150 m/min)
      normalized.push(
        Math.min(((data.meterage_per_minute || 0) / 150) * 100, 100),
      );

      // Top Speed (0-15 m/s)
      normalized.push(Math.min(((data.maximum_velocity || 0) / 15) * 100, 100));

      return normalized;
    },
    normalizePlayerMakerData(data) {
      const normalized = [];

      // Total Distance (0-10000m)
      normalized.push(
        Math.min(((data.distance_covered || 0) / 10000) * 100, 100),
      );

      // Work Rate (0-120 m/min)
      normalized.push(Math.min(((data.work_rate || 0) / 120) * 100, 100));

      // Total Touches (0-1000)
      normalized.push(Math.min(((data.total_touches || 0) / 1000) * 100, 100));

      // Total Passes (0-400)
      normalized.push(Math.min(((data.releases || 0) / 400) * 100, 100));

      // Session Duration (0-120 min)
      normalized.push(
        Math.min(((data.phase_duration_min || 0) / 120) * 100, 100),
      );

      // Top Speed (0-12 m/s)
      normalized.push(Math.min(((data.top_speed || 0) / 12) * 100, 100));

      return normalized;
    },
    getRawValue(label, datasetIndex) {
      const data =
        datasetIndex === 0
          ? this.gpsData
          : this.selectedComparison === "pb"
            ? this.personalBest
            : this.selectedComparison === "avg"
              ? this.seasonAverage
              : this.lastSession;

      if (!data) return 0;

      const fieldMap =
        this.gpsType === "catapult"
          ? {
              "Total Distance": "session_distance",
              "High Speed Running": "total_absolute_high_speed_distance",
              Accelerations: "accelerations_total_efforts",
              Decelerations: "decelerations_total_efforts",
              Intensity: "meterage_per_minute",
              "Top Speed": "maximum_velocity",
            }
          : {
              "Total Distance": "distance_covered",
              "Work Rate": "work_rate",
              "Total Touches": "total_touches",
              "Total Passes": "releases",
              "Session Duration": "phase_duration_min",
              "Top Speed": "top_speed",
            };

      const field = fieldMap[label];
      return field ? (data[field] || 0).toFixed(1) : 0;
    },
    getMetricUnit(label) {
      const unitMap = {
        "Total Distance": "m",
        "High Speed Running": "m",
        Accelerations: "",
        Decelerations: "",
        Intensity: " m/min",
        "Top Speed": " m/s",
        "Work Rate": " m/min",
        "Total Touches": "",
        "Total Passes": "",
        "Session Duration": " min",
      };
      return unitMap[label] || "";
    },
  },
};
</script>

<style lang="scss" scoped>
.radar-chart-container {
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;

  h4 {
    margin: 0;
    color: #333;
    font-weight: 600;
  }

  .chart-controls {
    .comparison-select {
      min-width: 150px;
    }
  }
}

.chart-wrapper {
  position: relative;
  margin: 1rem 0;
}

.chart-legend {
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin-top: 1rem;

  .legend-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.9rem;

    .legend-color {
      width: 16px;
      height: 16px;
      border-radius: 50%;
      border: 2px solid #fff;
    }

    &.current .legend-color {
      background-color: rgba(71, 169, 249, 1);
    }

    &.comparison .legend-color {
      background-color: rgba(255, 99, 132, 0.8);
    }
  }
}

@media (max-width: 768px) {
  .chart-header {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }

  .chart-legend {
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
  }
}
</style>
